import {
  GetContactInfoTypeResponse,
  GetLeadInvalidLostReasonPayload,
  GetInvalidLostReasonResponse,
  UpdateContactInfoTypePayload,
  GetExternalApiDetailResponse,
  GetExternalApiListResponse,
  GetExternalApiDetailPayload,
  GetExternalApiKeysResponse,
  RevokeExternalApiKeyPayload,
  CreateCustomLeadFormFieldPayload,
  CreateCustomLeadFormResponse,
  GetCustomLeadFormFields,
  UpdateCustomLeadFormFieldPayload,
  GetHostsResponse,
  GetAgentsResponse,
  GetAgentsPayload,
  UpdateSequenceCustomLeadFormFieldPayload,
  GetListCustomLeadFormFieldPayload,
  ChangeStatusCustomLeadFormFieldPayload,
  GetSalesPipelineByStatusResponse,
} from './types/settings.d'
import { createAsyncThunk } from '@reduxjs/toolkit'

import callAPI from 'api'
import {
  GenericSuccessResponse,
  GetAppSettingsPayload,
  GetAppSettingsResponse,
  GetCustomAddressConfigurationResponse,
  UpdateUserApprovalSettingsPayload,
  UpdateContactTypePayload,
  GetLeadStatusesResponse,
  GetIndustryResponse,
  UpdateIndustryPayload,
  GetContactTypeResponse,
  UpdateMasterSettingsPayload,
  UpdateMasterBranchSettingsPayload,
  UpdateCustomAddressConfigurationPayload,
  GetLeadSourceTypePayload,
  GetLeadSourceTypeResponse,
  GetLeadSourcePayload,
  GetLeadSourceResponse,
  SalesPipelineByStatusPayload,
} from 'services/types/settings'
import { Fail, Success } from 'store/types'
import { ObjectControlResponse } from './types/setting'

/**
 * Async function to get app settings
 */
export const getAppSettings = async (payload?: GetAppSettingsPayload) =>
  callAPI<GetAppSettingsResponse[], GetAppSettingsPayload>(
    'crm_core_get_app_settings',
    payload?.master_id ? [payload] : undefined,
    {
      requireAuthorization: true,
    },
  )

/**
 * Async function to get contact types
 */
export const getContactTypes = async () =>
  await callAPI<GetContactTypeResponse[]>('crm_get_contact_types', undefined, {
    requireAuthorization: true,
  })

/**
 * Async function to get contact types
 */
export const updateContactTypes = async (payload: UpdateContactTypePayload) =>
  await callAPI('crm_update_contact_types', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

/**
 * Async function to get contact info types
 */
export const getContactInfoTypes = createAsyncThunk('workflow/getContactInfoTypeList', async () => {
  const response = await callAPI<GetContactInfoTypeResponse[]>('crm_get_contact_info_types', undefined, {
    requireAuthorization: true,
  })

  if (response.status === 'SUCCESS') {
    return response.data
  }
})

type GetLeadStatusesPayload =
  | {
      shouldSoftUpdate: boolean
    }
  | undefined

/**
 * Async function to get lead statuses
 */
export const getLeadStatuses = createAsyncThunk<Fail | Success<GetLeadStatusesResponse>, GetLeadStatusesPayload>(
  'workflow/getLeadStatusList',
  async () =>
    await callAPI<GetLeadStatusesResponse>('crm_get_lead_statuses', undefined, {
      requireAuthorization: true,
    }),
)

export type GetLeadStatusGroupsResponse = Array<{
  lead_status_group_id: number
  name: string
  description: string
  is_end_pipeline: boolean
  is_start_pipeline: boolean
}>

export const getLeadStatusGroups = async () =>
  await callAPI<GetLeadStatusGroupsResponse>('crm_get_lead_status_groups', undefined, {
    requireAuthorization: true,
  })

export const updateLeadStatus = async (payload: {
  description: string
  id: string
  name: string
  lead_status_group_id: number
}) => callAPI('crm_update_lead_status', [payload], { requireAuthorization: true, reloadOnReauth: false })

export const createLeadStatus = async (payload: { description: string; name: string; lead_status_group_id: number }) =>
  callAPI('crm_create_lead_status', [payload], { requireAuthorization: true, reloadOnReauth: false })

/**
 * Async function to get Industry
 */
export const getIndustries = createAsyncThunk('workflow/getIndustryList', async () => {
  const response = await callAPI<GetIndustryResponse[]>('crm_get_industries', undefined, {
    requireAuthorization: true,
  })

  if (response.status === 'SUCCESS') {
    return response.data
  }
})

/**
 * Async function to update Industries
 */

export const updateIndustryList = createAsyncThunk(
  'workflow/updateIndustryList',
  async ({ payload, callback }: UpdateIndustryPayload) => {
    const response = await callAPI('crm_update_industries', [payload], {
      requireAuthorization: true,
      reloadOnReauth: false,
    })
    if (response.status === 'SUCCESS' || response.status === 'FAIL') {
      if (typeof callback === 'function') {
        callback(response.status)
      }
    }
  },
)
/*
 * Async function to get contact Info Types
 */

export const updateContactInfoTypes = createAsyncThunk(
  'workflow/updateContactInfoTypeList',
  async ({ payload, callback }: UpdateContactInfoTypePayload) => {
    const response = await callAPI('crm_update_contact_info_types', [payload], {
      requireAuthorization: true,
      reloadOnReauth: false,
    })

    if (response.status === 'SUCCESS' || response.status === 'FAIL') {
      if (typeof callback === 'function') {
        callback(response.status)
      }
    }
    return response
  },
)

/**
 * Async function to get DB table last modified time stamp
 */
export const getLastModifiedTimeStamp = async (mastId?: string) =>
  await callAPI<ObjectControlResponse>('crm_get_object_control', [{ master_id: mastId || null }])

export const updateUserApprovalSettings = async (payload: UpdateUserApprovalSettingsPayload) =>
  await callAPI<GenericSuccessResponse, UpdateUserApprovalSettingsPayload>(
    'crm_update_master_approval_config',
    [payload],
    { requireAuthorization: true, reloadOnReauth: false },
  )

/**
 * Generic service to update master settings
 */
export const updateMasterSettings = async (payload: UpdateMasterSettingsPayload[]) =>
  await callAPI('crm_update_master_settings', [payload], { requireAuthorization: true, reloadOnReauth: false })

/**
 * Generic service to update master settings
 */
export const updateMasterBranchSettings = async (payload: UpdateMasterBranchSettingsPayload[]) =>
  await callAPI('crm_update_master_branch_settings', [payload], { requireAuthorization: true, reloadOnReauth: false })

export const getListSalesPipelineByStatus = async (payload: SalesPipelineByStatusPayload) =>
  callAPI<GetSalesPipelineByStatusResponse, SalesPipelineByStatusPayload>(
    'crm_get_lead_processes_by_status',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const getLeadInvalidLostReasons = createAsyncThunk<
  Fail | Success<GetInvalidLostReasonResponse>,
  GetLeadInvalidLostReasonPayload
>(
  'workflow/getCloseInvalidReasons',
  async (payload) =>
    await callAPI<GetInvalidLostReasonResponse>('crm_get_lead_process_close_reason', [payload], {
      requireAuthorization: true,
    }),
)

export const deleteLeadStatus = async (payload: { lead_status_id: string }) =>
  await callAPI('crm_delete_lead_status', [payload], { requireAuthorization: true, reloadOnReauth: false })

export const getExternalApiList = createAsyncThunk<Fail | Success<GetExternalApiListResponse>>(
  'settings/getExternalApiList',
  async () =>
    await callAPI<GetExternalApiListResponse>('crm_get_external_api_list', undefined, {
      requireAuthorization: true,
    }),
)

export const getExternalApiDetail = createAsyncThunk<
  Fail | Success<GetExternalApiDetailResponse>,
  GetExternalApiDetailPayload
>(
  'settings/getExternalApiDetails',
  async (payload) =>
    await callAPI<GetExternalApiDetailResponse>('crm_get_external_api_details', [payload], {
      requireAuthorization: true,
    }),
)

export const getExternalApiKeys = createAsyncThunk<Fail | Success<GetExternalApiKeysResponse>>(
  'settings/getExternalApiKeys',
  async () =>
    await callAPI<GetExternalApiKeysResponse>('crm_get_external_api_keys', [], {
      requireAuthorization: true,
    }),
)

export const revokeApikey = async ({ api_key_id, callback }: RevokeExternalApiKeyPayload) => {
  const response = await callAPI<{ success: boolean }, RevokeExternalApiKeyPayload>(
    'crm_revoke_external_api_key',
    [{ api_key_id }],
    {
      requireAuthorization: true,
    },
  )
  callback?.(response.status)

  return response
}

export const generateApikey = async () =>
  await callAPI<{ success: boolean }>('crm_generate_external_api_key', [], {
    requireAuthorization: true,
  })

export const getCustomAddressConfigurationApi = async () =>
  await callAPI<GetCustomAddressConfigurationResponse>('crm_get_address_configuration', undefined, {
    requireAuthorization: true,
  })

export const getCustomAddressConfiguration = createAsyncThunk<Fail | Success<GetCustomAddressConfigurationResponse>>(
  'workflow/getCustomAddressConfiguration',
  getCustomAddressConfigurationApi,
)

export const updateCustomAddressConfiguration = async (payload: UpdateCustomAddressConfigurationPayload) =>
  await callAPI<{ success: boolean }, UpdateCustomAddressConfigurationPayload>(
    'crm_update_address_configuration',
    [payload],
    { requireAuthorization: true, reloadOnReauth: false },
  )

/**
 * Custom Lead Form Fields
 */
export const getListCustomLeadFormFields = async (payload: GetListCustomLeadFormFieldPayload) =>
  await callAPI<GetCustomLeadFormFields>('crm_get_custom_form_fields', [payload], {
    requireAuthorization: true,
  })

export const getCustomLeadFormFields = createAsyncThunk<
  Fail | Success<GetCustomLeadFormFields>,
  GetListCustomLeadFormFieldPayload
>('workflow/getCustomLeadFormFields', getListCustomLeadFormFields)

export const createCustomLeadFormField = async (payload: CreateCustomLeadFormFieldPayload) =>
  await callAPI<CreateCustomLeadFormResponse, CreateCustomLeadFormFieldPayload>(
    'crm_create_custom_form_field',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const updateCustomLeadFormField = async (payload: UpdateCustomLeadFormFieldPayload) =>
  await callAPI<CreateCustomLeadFormResponse, UpdateCustomLeadFormFieldPayload>(
    'crm_update_custom_form_fields',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const updateSequenceCustomLeadFormField = async (payload: UpdateSequenceCustomLeadFormFieldPayload) =>
  await callAPI<{ success: boolean }, UpdateSequenceCustomLeadFormFieldPayload>(
    'crm_sequence_custom_form_fields',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const changeStatusCustomLeadFormField = async (payload: ChangeStatusCustomLeadFormFieldPayload) =>
  await callAPI<{ success: boolean }, ChangeStatusCustomLeadFormFieldPayload>(
    'crm_activate_custom_form_fields',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const getLeadSourceTypes = async (payload: GetLeadSourceTypePayload) =>
  callAPI<{ success: boolean } & GetLeadSourceTypeResponse, GetLeadSourceTypePayload>(
    'crm_get_lead_source_types',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const getLeadSources = async (payload: GetLeadSourcePayload) =>
  callAPI<{ success: boolean } & GetLeadSourceResponse, GetLeadSourcePayload>('crm_get_lead_sources', [payload], {
    requireAuthorization: true,
  })

/**
 * CAMS Sales Agent
 */
export const getCamsHosts = createAsyncThunk<Fail | Success<GetHostsResponse>>(
  'cams/getHosts',
  async () => await callAPI<GetHostsResponse>('crm_get_cams_hosts', undefined, { requireAuthorization: true }),
)

export const getCamsAgents = async (payload: GetAgentsPayload) =>
  await callAPI<GetAgentsResponse, GetAgentsPayload>('crm_get_cams_sales_agents', [payload], {
    requireAuthorization: true,
  })
